// assets/tcard-accent.js
// Samples homepage article-card artwork and derives its accent color as a
// DEEP, SATURATED version of the art's own dominant hue — each card keeps
// the hue of its own image, darkened for white type, with saturation lifted
// so the tint reads as a clean rich color instead of a muddy gray (average
// artwork colors are inherently low-saturation, so they need a floor).
// Cards with the .tcard--caramel (pinned brand tint) or .tcard--story
// (keynote light card, no gradient) modifiers are skipped.

const SATURATION_MIN = 0.38; // lift the tint so it never looks muddy
const SATURATION_MAX = 0.55; // dial back over-saturated art
const LUMINANCE_MIN = 0.32; // dark enough for white type
const LUMINANCE_MAX = 0.42; // but not pitch black

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) {
      h = (g - b) / d + (g < b ? 6 : 0);
    } else if (max === g) {
      h = (b - r) / d + 2;
    } else {
      h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return [h, s, l];
}

function hslToRgb(h, s, l) {
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  if (s === 0) {
    return [l * 255, l * 255, l * 255];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    hue2rgb(p, q, h + 1 / 3) * 255,
    hue2rgb(p, q, h) * 255,
    hue2rgb(p, q, h - 1 / 3) * 255,
  ];
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function sampleCard(card) {
  const img = card.querySelector('img');
  if (!img) return;

  const probe = new Image();
  probe.crossOrigin = 'anonymous';
  probe.onload = () => {
    try {
      const scale = 0.08;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(probe.naturalWidth * scale);
      canvas.height = Math.round(probe.naturalHeight * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(probe, 0, 0, canvas.width, canvas.height);

      // Average the bottom 20% band — the zone the gradient sits behind.
      const y0 = Math.floor(canvas.height * 0.8);
      const data = ctx.getImageData(0, y0, canvas.width, canvas.height - y0).data;
      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      for (let i = 0; i < data.length; i += 4) {
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        n += 1;
      }

      const [h, s, l] = rgbToHsl(r / n, g / n, b / n);
      // Keep the art's own hue; only tame saturation and darken to read.
      const sClamped = clamp(s, SATURATION_MIN, SATURATION_MAX);
      const lClamped = clamp(l, LUMINANCE_MIN, LUMINANCE_MAX);
      const [cr, cg, cb] = hslToRgb(h, sClamped, lClamped);

      card.style.setProperty(
        '--tcard-accent',
        `rgb(${Math.round(cr)}, ${Math.round(cg)}, ${Math.round(cb)})`
      );
    } catch (error) {
      // Sampling is best-effort; the caramel fallback tint stays in place.
    }
  };
  probe.src = img.currentSrc || img.src;
}

function initTcards() {
  document.querySelectorAll('.tcard:not(.tcard--caramel):not(.tcard--story)').forEach((card) => {
    if (card.dataset.navInited === 'true') return;
    card.dataset.navInited = 'true';
    sampleCard(card);
  });
}

initTcards();
document.addEventListener('site:navigation:end', initTcards);